<template>
  <div class="max-w-2xl mx-auto space-y-12 pb-16">
    <section aria-labelledby="organization-heading">
      <h1 id="organization-heading" class="text-2xl font-semibold text-gray-900 dark:text-white">
        Organization
      </h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Runners, members and the plan are shared by everyone in the organization.
      </p>

      <div
        v-if="!organization"
        class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500 dark:border-white/10 dark:bg-gray-900/60 dark:text-gray-400 shadow-xs"
        role="status"
      >
        {{ organizations.error ?? 'Loading organization…' }}
      </div>

      <div
        v-else
        class="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      >
        <form
          v-if="isOwner"
          class="p-4 sm:px-6 border-b border-gray-100 dark:border-white/10"
          novalidate
          @submit.prevent="submitRename"
        >
          <label
            for="organization-rename"
            class="block text-sm font-medium text-gray-900 dark:text-white"
            >Name</label
          >
          <div class="mt-1 flex gap-3">
            <input
              id="organization-rename"
              v-model="nameDraft"
              type="text"
              :maxlength="ORGANIZATION_NAME_MAX_LENGTH"
              required
              autocomplete="organization"
              :disabled="renaming"
              :aria-invalid="Boolean(renameError)"
              :aria-describedby="renameError ? 'organization-rename-error' : undefined"
              class="block min-w-0 flex-1 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500"
              :class="renameError ? 'outline-red-500 dark:outline-red-400' : ''"
            />
            <button
              type="submit"
              :disabled="renaming || nameDraft.trim() === organization.name"
              class="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
            >
              {{ renaming ? 'Saving…' : 'Save' }}
            </button>
          </div>
          <p
            v-if="renameError"
            id="organization-rename-error"
            class="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {{ renameError }}
          </p>
        </form>

        <dl class="divide-y divide-gray-100 text-sm dark:divide-white/10">
          <div v-if="!isOwner" class="flex flex-wrap justify-between gap-2 p-4 sm:px-6">
            <dt class="text-gray-500 dark:text-gray-400">Name</dt>
            <dd class="font-medium text-gray-900 dark:text-white" data-testid="organization-name">
              {{ organization.name }}
            </dd>
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2 p-4 sm:px-6">
            <dt class="text-gray-500 dark:text-gray-400">Your role</dt>
            <dd class="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span class="hidden sm:inline text-xs">{{
                ROLE_INFO[organization.role].description
              }}</span>
              <span
                class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium inset-ring"
                :class="ROLE_INFO[organization.role].badge"
                >{{ roleLabel(organization.role) }}</span
              >
            </dd>
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2 p-4 sm:px-6">
            <dt class="text-gray-500 dark:text-gray-400">Plan</dt>
            <dd class="flex flex-wrap items-center gap-3" data-testid="organization-plan">
              <span class="font-medium text-gray-900 dark:text-white">{{ planLabel }}</span>
              <RouterLink
                v-if="isOwner"
                :to="hasPlan ? { name: 'Profile', hash: '#billing' } : { name: 'Billing' }"
                class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                >{{ hasPlan ? 'Manage billing' : 'Choose a plan' }}</RouterLink
              >
              <span v-else class="text-xs text-gray-500 dark:text-gray-400"
                >Managed by the owner</span
              >
            </dd>
          </div>
          <div v-if="organization.default" class="flex flex-wrap justify-between gap-2 p-4 sm:px-6">
            <dt class="text-gray-500 dark:text-gray-400">Default</dt>
            <dd class="text-gray-600 dark:text-gray-300">
              Selected after sign-in and used by clients that don't choose an organization.
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <section v-if="organization" aria-labelledby="members-heading">
      <h2 id="members-heading" class="text-xl font-semibold text-gray-900 dark:text-white">
        Members
      </h2>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        <template v-if="isOwner"
          >Change roles or remove people. The owner can't be changed.</template
        >
        <template v-else>People who share this organization's runners.</template>
      </p>

      <div
        class="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      >
        <div
          v-if="membersLoading"
          class="p-6 text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          Loading members…
        </div>
        <div v-else-if="membersError" class="p-6" role="alert">
          <p class="text-sm text-red-600 dark:text-red-400">{{ membersError }}</p>
          <button
            type="button"
            class="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
            @click="loadMembers"
          >
            Try again
          </button>
        </div>
        <ul
          v-else
          role="list"
          class="divide-y divide-gray-100 dark:divide-white/10"
          aria-label="Members"
        >
          <li
            v-for="member in members"
            :key="member.userId"
            class="flex flex-wrap items-center gap-x-3 gap-y-2 p-4 sm:flex-nowrap sm:px-6"
            :data-member-id="member.userId"
          >
            <img
              class="size-9 shrink-0 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"
              :src="gravatarUrl(member.username, { size: 72, defaultImage: 'retro' })"
              alt=""
            />
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">
                {{ memberName(member) }}
                <span v-if="isMe(member)" class="font-normal text-gray-500 dark:text-gray-400"
                  >(you)</span
                >
              </p>
              <p
                v-if="memberName(member) !== member.username"
                class="truncate text-xs text-gray-500 dark:text-gray-400"
              >
                {{ member.username }}
              </p>
            </div>
            <div
              v-if="isOwner && member.role !== 'OWNER'"
              class="flex w-full shrink-0 justify-end gap-2 sm:w-auto"
            >
              <label :for="`role-${member.userId}`" class="sr-only"
                >Role of {{ memberName(member) }}</label
              >
              <div class="grid shrink-0 grid-cols-1">
                <select
                  :id="`role-${member.userId}`"
                  :value="member.role"
                  :disabled="busyMemberId === member.userId"
                  class="col-start-1 row-start-1 appearance-none rounded-md bg-white py-1 pr-7 pl-2.5 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:opacity-50 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:*:bg-gray-800 dark:focus:outline-indigo-500"
                  @change="
                    onRoleChange(
                      member,
                      ($event.target as HTMLSelectElement).value as InvitableRole
                    )
                  "
                >
                  <option v-for="role in INVITABLE_ROLES" :key="role" :value="role">
                    {{ roleLabel(role) }}
                  </option>
                </select>
                <ChevronDownIcon
                  class="pointer-events-none col-start-1 row-start-1 mr-1.5 size-4 self-center justify-self-end text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <button
                type="button"
                class="shrink-0 rounded-md bg-white px-2.5 py-1 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 disabled:opacity-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/10 dark:hover:bg-red-500/10 cursor-pointer"
                :disabled="busyMemberId === member.userId"
                :aria-label="`Remove ${memberName(member)}`"
                @click="askRemove(member)"
              >
                Remove
              </button>
            </div>
            <span
              v-else
              class="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium inset-ring"
              :class="ROLE_INFO[member.role].badge"
              >{{ roleLabel(member.role) }}</span
            >
          </li>
        </ul>
      </div>
    </section>

    <section v-if="organization && isOwner" aria-labelledby="invitations-heading">
      <div class="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="invitations-heading" class="text-xl font-semibold text-gray-900 dark:text-white">
            Invitations
          </h2>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Invite people by email. Links are single-use and expire after a week.
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
          :disabled="sendWaitSeconds > 0"
          data-testid="open-invite"
          @click="openInvite"
        >
          <UserPlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
          Invite member
        </button>
      </div>

      <p
        v-if="sendWaitSeconds > 0 && !inviteOpen"
        class="mt-4 text-sm text-amber-700 dark:text-amber-300"
        role="status"
        data-testid="invite-wait"
      >
        {{ sendWaitMessage }}
      </p>
      <div
        v-if="verificationRequired && !inviteOpen"
        class="mt-4 space-y-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 sm:px-6 dark:border-indigo-500/30 dark:bg-indigo-500/10"
        data-testid="invite-verify-email"
      >
        <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ VERIFY_TO_INVITE }}</p>
        <p class="text-sm text-gray-700 dark:text-gray-300">{{ verifyHint }}</p>
        <EmailVerificationForm @verified="onEmailVerified" />
      </div>

      <div
        class="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      >
        <div
          v-if="invitationsLoading"
          class="p-6 text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          Loading invitations…
        </div>
        <div v-else-if="invitationsError" class="p-6" role="alert">
          <p class="text-sm text-red-600 dark:text-red-400">{{ invitationsError }}</p>
          <button
            type="button"
            class="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
            @click="loadInvitations"
          >
            Try again
          </button>
        </div>
        <div v-else-if="invitations.length === 0" class="p-6 text-center">
          <EnvelopeIcon
            class="mx-auto size-10 text-gray-300 dark:text-gray-600"
            aria-hidden="true"
          />
          <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
            No pending invitations
          </p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Invited people appear here until they accept.
          </p>
        </div>
        <ul
          v-else
          role="list"
          class="divide-y divide-gray-100 dark:divide-white/10"
          aria-label="Pending invitations"
        >
          <li
            v-for="invitation in invitations"
            :key="invitation.id"
            class="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:px-6"
            :data-invitation-id="invitation.id"
          >
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">
                  {{ invitation.email }}
                </p>
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium inset-ring"
                  :class="ROLE_INFO[invitation.role].badge"
                  >{{ roleLabel(invitation.role) }}</span
                >
                <span
                  v-if="isExpired(invitation)"
                  class="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 inset-ring inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20"
                  >Expired</span
                >
              </div>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ isExpired(invitation) ? 'Expired' : 'Expires' }}
                <time :datetime="invitation.expiresAt">{{ formatDate(invitation.expiresAt) }}</time>
              </p>
            </div>
            <div class="flex shrink-0 gap-2">
              <button
                type="button"
                class="rounded-md bg-white px-2.5 py-1 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/10 dark:hover:bg-white/20 cursor-pointer"
                :disabled="busyInvitationId === invitation.id || sendWaitSeconds > 0"
                :aria-label="`Resend invitation to ${invitation.email}`"
                @click="resend(invitation)"
              >
                {{ busyInvitationId === invitation.id ? 'Sending…' : 'Resend' }}
              </button>
              <button
                type="button"
                class="rounded-md bg-white px-2.5 py-1 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 disabled:opacity-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/10 dark:hover:bg-red-500/10 cursor-pointer"
                :disabled="busyInvitationId === invitation.id"
                :aria-label="`Revoke invitation to ${invitation.email}`"
                @click="askRevoke(invitation)"
              >
                Revoke
              </button>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <section v-if="organization && !isOwner" aria-labelledby="leave-heading">
      <div
        class="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      >
        <div>
          <h2 id="leave-heading" class="text-sm font-semibold text-gray-900 dark:text-white">
            Leave organization
          </h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You lose access to its runners. The owner can invite you again.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 self-start rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 disabled:opacity-50 sm:self-center dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/10 dark:hover:bg-red-500/10 cursor-pointer"
          :disabled="!members.some(isMe)"
          @click="askLeave"
        >
          Leave
        </button>
      </div>
    </section>

    <ModalPanel :open="inviteOpen" :initial-focus="emailInput" @close="closeInvite">
      <form novalidate @submit.prevent="submitInvite">
        <DialogTitle as="h3" class="text-base font-semibold text-gray-900 dark:text-white">
          Invite member
        </DialogTitle>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          We email a link to join {{ organization?.name }}. The person signs in or registers with
          this address to accept.
        </p>
        <div class="mt-5 space-y-6">
          <div>
            <label
              for="invite-email"
              class="block text-sm font-medium text-gray-900 dark:text-white"
              >Email</label
            >
            <input
              id="invite-email"
              ref="emailInput"
              v-model="inviteForm.email"
              type="email"
              required
              autocomplete="off"
              placeholder="colleague@example.com"
              :disabled="inviting"
              :aria-invalid="Boolean(inviteEmailError)"
              :aria-describedby="inviteEmailError ? 'invite-email-error' : undefined"
              class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              :class="inviteEmailError ? 'outline-red-500 dark:outline-red-400' : ''"
            />
            <p
              v-if="inviteEmailError"
              id="invite-email-error"
              class="mt-1 text-sm text-red-600 dark:text-red-400"
            >
              {{ inviteEmailError }}
            </p>
          </div>
          <fieldset>
            <legend class="text-sm font-medium text-gray-900 dark:text-white">Role</legend>
            <div
              class="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-white/10 dark:border-white/10"
            >
              <label
                v-for="role in INVITABLE_ROLES"
                :key="role"
                :for="`invite-role-${role}`"
                class="flex cursor-pointer gap-3 px-3 py-2.5"
              >
                <input
                  :id="`invite-role-${role}`"
                  v-model="inviteForm.role"
                  :value="role"
                  type="radio"
                  name="invite-role"
                  :disabled="inviting"
                  class="mt-1 size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5 dark:checked:bg-indigo-500"
                />
                <span class="text-sm/6">
                  <span class="block font-medium text-gray-900 dark:text-white">{{
                    roleLabel(role)
                  }}</span>
                  <span class="block text-gray-500 dark:text-gray-400">{{
                    ROLE_INFO[role].description
                  }}</span>
                </span>
              </label>
            </div>
          </fieldset>
        </div>
        <p v-if="inviteError" class="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {{ inviteError }}
        </p>
        <p
          v-if="sendWaitSeconds > 0"
          class="mt-4 text-sm text-amber-700 dark:text-amber-300"
          role="status"
          data-testid="invite-wait"
        >
          {{ sendWaitMessage }}
        </p>
        <div
          v-if="verificationRequired"
          class="mt-4 space-y-3 rounded-md border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-500/30 dark:bg-indigo-500/10"
          data-testid="invite-verify-email"
        >
          <p class="text-sm font-semibold text-gray-900 dark:text-white" role="alert">
            {{ VERIFY_TO_INVITE }}
          </p>
          <p class="text-sm text-gray-700 dark:text-gray-300">{{ verifyHint }}</p>
          <EmailVerificationForm @verified="onEmailVerified" />
        </div>
        <div class="mt-6 sm:flex sm:flex-row-reverse gap-3">
          <button
            type="submit"
            :disabled="inviting || sendWaitSeconds > 0 || verificationRequired"
            class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-50 sm:w-auto dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
          >
            {{ inviting ? 'Sending…' : 'Send invitation' }}
          </button>
          <button
            type="button"
            :disabled="inviting"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
            @click="closeInvite"
          >
            Cancel
          </button>
        </div>
      </form>
    </ModalPanel>

    <ConfirmDialog
      :open="confirm !== null"
      :title="confirm?.title ?? ''"
      :message="confirm?.message ?? ''"
      :confirm-label="confirm?.confirmLabel ?? ''"
      :busy-label="confirm?.busyLabel ?? ''"
      :busy="confirming"
      :error="confirmError"
      @cancel="confirm = null"
      @confirm="runConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DialogTitle } from '@headlessui/vue';
import { EnvelopeIcon, UserPlusIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import ModalPanel from '@/components/ModalPanel.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import EmailVerificationForm from '@/components/EmailVerificationForm.vue';
import {
  changeMemberRole,
  createInvitation,
  type InvitableRole,
  listInvitations,
  listMembers,
  type OrganizationInvitation,
  type OrganizationMember,
  removeMember,
  renameOrganization,
  resendInvitation,
  revokeInvitation,
} from '@/api/organizations';
import { isEmailNotVerified, retryAfterSeconds } from '@/lib/email-verification';
import {
  formatWait,
  INVITABLE_ROLES,
  ORGANIZATION_NAME_MAX_LENGTH,
  organizationApiError,
  ROLE_INFO,
  roleLabel,
  validateOrganizationName,
} from '@/lib/organizations';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { formatEnum } from '@/lib/format';
import gravatarUrl from '@/lib/gravatar';

const auth = useAuthStore();
const organizations = useOrganizationStore();
const subscriptionStore = useSubscriptionStore();
const toast = useToastStore();
const router = useRouter();

const organization = computed(() => organizations.current);
const isOwner = computed(() => organizations.canManageOrganization);
// The page is keyed by organization in Main, so the id stays fixed for this instance.
const organizationId = organizations.selectedId;

const hasPlan = computed(() =>
  ['ACTIVE', 'ON_HOLD'].includes(subscriptionStore.subscription?.status ?? '')
);
const planLabel = computed(() => {
  const subscription = subscriptionStore.subscription;
  if (!hasPlan.value || !subscription) return 'No active plan';
  const flavor = formatEnum(subscription.flavor);
  return subscription.status === 'ACTIVE'
    ? flavor
    : `${flavor} · ${formatEnum(subscription.status)}`;
});

// ---- Rename ----
const nameDraft = ref(organization.value?.name ?? '');
const renaming = ref(false);
const renameError = ref<string | null>(null);

async function submitRename() {
  if (!organizationId) return;
  renameError.value = validateOrganizationName(nameDraft.value);
  if (renameError.value) return;
  renaming.value = true;
  try {
    const renamed = await renameOrganization(organizationId, nameDraft.value.trim());
    organizations.upsert(renamed);
    nameDraft.value = renamed.name;
    toast.success('Organization renamed.');
  } catch (e) {
    renameError.value = organizationApiError(e, 'rename the organization').message;
  } finally {
    renaming.value = false;
  }
}

// ---- Members ----
const members = ref<OrganizationMember[]>([]);
const membersLoading = ref(true);
const membersError = ref<string | null>(null);
const busyMemberId = ref<string | null>(null);

function memberName(member: OrganizationMember) {
  const full = [member.givenName, member.familyName].filter(Boolean).join(' ');
  return full || member.username;
}

function isMe(member: OrganizationMember) {
  return member.username.toLowerCase() === (auth.username ?? '').toLowerCase();
}

async function loadMembers() {
  if (!organizationId) return;
  membersLoading.value = true;
  membersError.value = null;
  try {
    const list = await listMembers(organizationId);
    const order = { OWNER: 0, EDITOR: 1, VIEWER: 2 };
    members.value = [...list].sort(
      (a, b) => order[a.role] - order[b.role] || memberName(a).localeCompare(memberName(b))
    );
  } catch (e) {
    membersError.value = organizationApiError(e, 'load the members').message;
  } finally {
    membersLoading.value = false;
  }
}

async function onRoleChange(member: OrganizationMember, role: InvitableRole) {
  if (!organizationId || role === member.role) return;
  const previous = member.role;
  member.role = role;
  busyMemberId.value = member.userId;
  try {
    await changeMemberRole(organizationId, member.userId, role);
    toast.success(`${memberName(member)} is now ${roleLabel(role)}.`);
  } catch (e) {
    member.role = previous;
    toast.error(organizationApiError(e, 'change the role').message);
  } finally {
    busyMemberId.value = null;
  }
}

// ---- Invitations ----
const invitations = ref<OrganizationInvitation[]>([]);
const invitationsLoading = ref(true);
const invitationsError = ref<string | null>(null);
const busyInvitationId = ref<string | null>(null);
const now = ref(Date.now());
let disposed = false;

// ---- Sending limits: a verified email is required, and the server limits invitation emails ----
const VERIFY_TO_INVITE = 'Verify your email address before inviting members';
const verifyHint = computed(
  () =>
    `Enter the 6-digit code we emailed to ${auth.username ?? 'your address'}, or request a new one. Then send the invitation again.`
);
const verificationNeeded = ref(false);
const verificationRequired = computed(
  () => verificationNeeded.value && auth.emailVerified !== true
);

const sendBlockedUntilMs = ref<number | null>(null);
const clockMs = ref(Date.now());
let waitTimer: ReturnType<typeof setInterval> | null = null;
const sendWaitSeconds = computed(() =>
  sendBlockedUntilMs.value
    ? Math.max(0, Math.ceil((sendBlockedUntilMs.value - clockMs.value) / 1000))
    : 0
);
const sendWaitMessage = computed(
  () => `Too many invitation emails. You can send more in ${formatWait(sendWaitSeconds.value)}.`
);

function stopWaitTimer() {
  if (waitTimer) clearInterval(waitTimer);
  waitTimer = null;
}

function waitBeforeSending(seconds: number) {
  clockMs.value = Date.now();
  sendBlockedUntilMs.value = clockMs.value + seconds * 1000;
  stopWaitTimer();
  waitTimer = setInterval(() => {
    clockMs.value = Date.now();
    if (sendWaitSeconds.value === 0) stopWaitTimer();
  }, 1000);
}

function handleSendRefusal(e: unknown, account: number): boolean {
  if (isEmailNotVerified(e)) {
    if (auth.markEmailNotVerified(account)) verificationNeeded.value = true;
    return true;
  }
  if ((e as { response?: { status?: number } }).response?.status === 429) {
    waitBeforeSending(retryAfterSeconds(e) ?? 60);
    return true;
  }
  return false;
}

function onEmailVerified() {
  verificationNeeded.value = false;
  inviteError.value = null;
}

function isExpired(invitation: OrganizationInvitation) {
  return Date.parse(invitation.expiresAt) <= now.value;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

async function loadInvitations() {
  if (!organizationId) return;
  invitationsLoading.value = true;
  invitationsError.value = null;
  try {
    invitations.value = await listInvitations(organizationId);
    now.value = Date.now();
  } catch (e) {
    invitationsError.value = organizationApiError(e, 'load the invitations').message;
  } finally {
    invitationsLoading.value = false;
  }
}

const inviteOpen = ref(false);
const inviting = ref(false);
const inviteForm = reactive({ email: '', role: 'EDITOR' as InvitableRole });
const inviteEmailError = ref<string | null>(null);
const inviteError = ref<string | null>(null);
const emailInput = ref<HTMLInputElement | null>(null);

function openInvite() {
  inviteForm.email = '';
  inviteForm.role = 'EDITOR';
  inviteEmailError.value = null;
  inviteError.value = null;
  inviteOpen.value = true;
}

function closeInvite() {
  if (inviting.value) return;
  inviteOpen.value = false;
}

async function submitInvite() {
  if (!organizationId) return;
  const email = inviteForm.email.trim();
  inviteEmailError.value = /^[^\s@]+@[^\s@]+$/.test(email) ? null : 'Enter a valid email address.';
  inviteError.value = null;
  if (inviteEmailError.value) {
    emailInput.value?.focus();
    return;
  }
  inviting.value = true;
  const account = auth.accountContext();
  try {
    const created = await createInvitation(organizationId, email, inviteForm.role);
    invitations.value = [created, ...invitations.value.filter((i) => i.email !== created.email)];
    now.value = Date.now();
    inviteOpen.value = false;
    toast.success(`Invitation sent to ${created.email}.`);
  } catch (e) {
    if (disposed || handleSendRefusal(e, account)) return;
    const error = organizationApiError(e, 'send the invitation');
    if (error.field === 'email') inviteEmailError.value = error.message;
    else inviteError.value = error.message;
    if (error.code === 'EMAIL_SEND_FAILED') void loadInvitations();
  } finally {
    inviting.value = false;
  }
}

async function resend(invitation: OrganizationInvitation) {
  if (!organizationId) return;
  busyInvitationId.value = invitation.id;
  const account = auth.accountContext();
  try {
    const renewed = await resendInvitation(organizationId, invitation.id);
    invitations.value = invitations.value.map((i) => (i.id === renewed.id ? renewed : i));
    now.value = Date.now();
    toast.success(`Invitation resent to ${invitation.email}. The previous link no longer works.`);
  } catch (e) {
    if (disposed || handleSendRefusal(e, account)) return;
    const error = organizationApiError(e, 'resend the invitation');
    toast.error(error.message);
    if (error.status === 404 || error.code === 'EMAIL_SEND_FAILED') void loadInvitations();
  } finally {
    busyInvitationId.value = null;
  }
}

// ---- Confirmations: remove member, revoke invitation, leave ----
interface PendingConfirm {
  title: string;
  message: string;
  confirmLabel: string;
  busyLabel: string;
  action: () => Promise<void>;
  failure: string;
}

const confirm = ref<PendingConfirm | null>(null);
const confirming = ref(false);
const confirmError = ref<string | null>(null);

function ask(pending: PendingConfirm) {
  confirmError.value = null;
  confirm.value = pending;
}

async function runConfirm() {
  const pending = confirm.value;
  if (!pending) return;
  confirming.value = true;
  confirmError.value = null;
  try {
    await pending.action();
    confirm.value = null;
  } catch (e) {
    confirmError.value = organizationApiError(e, pending.failure).message;
  } finally {
    confirming.value = false;
  }
}

function askRemove(member: OrganizationMember) {
  ask({
    title: `Remove ${memberName(member)}?`,
    message: `${member.username} loses access to this organization's runners immediately.`,
    confirmLabel: 'Remove member',
    busyLabel: 'Removing…',
    failure: 'remove the member',
    action: async () => {
      await removeMember(organizationId!, member.userId);
      members.value = members.value.filter((m) => m.userId !== member.userId);
      toast.success(`${memberName(member)} was removed.`);
    },
  });
}

function askRevoke(invitation: OrganizationInvitation) {
  ask({
    title: `Revoke invitation to ${invitation.email}?`,
    message: 'The link in the email stops working. You can invite the address again later.',
    confirmLabel: 'Revoke invitation',
    busyLabel: 'Revoking…',
    failure: 'revoke the invitation',
    action: async () => {
      await revokeInvitation(organizationId!, invitation.id);
      invitations.value = invitations.value.filter((i) => i.id !== invitation.id);
      toast.success(`Invitation to ${invitation.email} revoked.`);
    },
  });
}

function askLeave() {
  const name = organization.value?.name ?? 'this organization';
  ask({
    title: `Leave ${name}?`,
    message:
      'You lose access to its runners immediately. To return, the owner must invite you again.',
    confirmLabel: 'Leave organization',
    busyLabel: 'Leaving…',
    failure: 'leave the organization',
    action: async () => {
      await removeMember(organizationId!, members.value.find(isMe)!.userId);
      if (organizations.defaultOrganizationId)
        organizations.select(organizations.defaultOrganizationId);
      toast.success(`You left ${name}.`);
      try {
        await organizations.load();
      } finally {
        await router.replace({ name: 'Dashboard', force: true });
      }
    },
  });
}

onBeforeUnmount(() => {
  disposed = true;
  stopWaitTimer();
});

onMounted(() => {
  void loadMembers();
  if (isOwner.value) void loadInvitations();
  if (subscriptionStore.organizationId !== organizationId || !subscriptionStore.subscription) {
    subscriptionStore.fetchSubscription().catch(() => undefined);
  }
});
</script>
